import {
  ArrowBackOutlined,
  ButtonDS,
  DialogModalDS,
  SendOutlined,
  Spacer,
  TableDS,
  Tag,
  TextCapitalized,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useCallback, useState } from "react";
import "../../../../i18n/i18n";
import {
  DepartmentType,
  DepartmentTypeDisplayed,
} from "../../departments/departmentsApi";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { selectCompany } from "../../homeSlice";
import {
  selectDepartments,
  selectGetDepartmentsByCompanyStatus,
} from "../../departments/departmentsSlice";
import { getDepartmentsByCompanyAsync } from "../../departments/departmentsSlice";
import { useEffect } from "react";
import { EmptyTable } from "@qivia/ui/src/assets/assets";
import type { TableHeaderProps } from "@qivia/ui/src/designSystem/components/table/TableHeader";

export const AddUserDepartmentModal = (props: {
  onSubmit: (value: DepartmentType[]) => void;
  onClose: () => void;
  isUpdate?: boolean;
  isVisible: boolean;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const departments = useAppSelector(selectDepartments);
  const getDepartmentsByCompanyStatus = useAppSelector(
    selectGetDepartmentsByCompanyStatus,
  );
  const [rowsChecked, setRowsChecked] = useState<DepartmentType[]>([]);

  const { onSubmit, onClose, isVisible } = props;

  const onSelectSubmit = useCallback(() => {
    if (rowsChecked.length === 0) {
      return;
    }
    onSubmit(rowsChecked);
  }, [onSubmit, rowsChecked]);

  const headers = {
    name: {
      text: t("users.modal.department.table.name"),
    },
    vehiclesCount: {
      text: t("users.modal.department.table.vehiclesCount"),
    },
  };

  const render =
    (row: DepartmentType) => (key: keyof DepartmentTypeDisplayed) => {
      switch (key) {
        case "name":
          return (
            <Tag
              textColor={colors["colors/text/black"]}
              backgroundColor={
                colors["colors/surfaces/background/background_level0"]
              }
              text={row[key]}
              borderColor={colors["colors/borders/cells/cells"]}
            />
          );
        case "vehiclesCount":
          return (
            (row[key] ?? "0") +
            " " +
            (Number(row[key]) > 1
              ? t("users.modal.department.table.vehiclesCount.multiple.unit")
              : t("users.modal.department.table.vehiclesCount.unit"))
          );
      }
    };

  useEffect(() => {
    if (company && getDepartmentsByCompanyStatus === "idle") {
      void dispatch(getDepartmentsByCompanyAsync(company.uuid));
    }
  }, [dispatch, company, getDepartmentsByCompanyStatus]);

  if (!props.isVisible) return null;

  return (
    <DialogModalDS
      onClose={onClose}
      visible={isVisible}
      width={38}
      title={t(`users.modal.department.title`)}
      subTitle={t(`users.modal.department.subtitle`)}
      bottomButtons={{
        leftButton: (
          <ButtonDS
            leftIcon={<ArrowBackOutlined />}
            text={t("users.modal.role.return")}
            format={"fill"}
            buttonType={"secondary"}
            onClick={onClose}
          />
        ),
        rightButton: (
          <ButtonDS
            rightIcon={<SendOutlined />}
            text={
              props.isUpdate
                ? t("users.modal.role.update")
                : t("users.modal.role.submit")
            }
            format={"fill"}
            buttonType={"primary"}
            onClick={onSelectSubmit}
            disabled={rowsChecked.length === 0}
          />
        ),
      }}
    >
      <TableDS<keyof DepartmentTypeDisplayed, DepartmentType>
        data={departments}
        headers={headers as Record<keyof DepartmentType, TableHeaderProps>}
        render={render}
        rowsChecked={rowsChecked}
        setRowsChecked={setRowsChecked}
        width={35}
        height={25}
        header-checkbox-data-cy="header-checkbox-data-cy"
        emptyContent={
          <StyledFlex>
            <Spacer x={1.5} />
            <StyledEmptyTable>
              <Spacer y={2} />
              <EmptyTable />
              <Spacer y={1.5} />
              <StyledTitleEmptyTable>
                <TextCapitalized>
                  {t("users.modal.department.listEmpty.title")}
                </TextCapitalized>
              </StyledTitleEmptyTable>
              <Spacer y={1} />
            </StyledEmptyTable>
            <Spacer x={1.5} />
          </StyledFlex>
        }
      />
    </DialogModalDS>
  );
};

const StyledFlex = styled.div`
  display: flex;
`;

const StyledTitleEmptyTable = styled.div`
  display: flex;
  ${typographies["Header/H1"]};
  color: ${colors["colors/text/black"]};
`;

const StyledEmptyTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  text-align: center;
`;
